import React, { useState, useEffect, useCallback } from 'react';
import './AdminAuditLogs.css';
import SvgIcon from '../../components/SvgIcon';

const API_BASE = process.env.REACT_APP_API_URL || '';

// Helper to get today's date formatted as YYYY-MM-DD in local application time (prevents UTC timezone shift)
export const getTodayDateStr = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Helper to format date for filter header in CSV (YYYY-MM-DD -> DD/MM/YYYY)
export const formatDateForFilter = (dateStr) => {
  if (!dateStr) return 'All';
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [y, m, d] = dateStr.split('-');
    return `${d}/${m}/${y}`;
  }
  return dateStr;
};

// Helper to format action names (e.g. ACCOUNT_DEACTIVATED -> Account Deactivated)
export const formatAction = (action) => {
  if (!action) return '';
  return action
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

// Helper for exact timestamp formatting (YYYY-MM-DD HH:mm:ss)
export const formatExactTimestamp = (ts) => {
  if (!ts) return '';
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(ts)) {
    return ts;
  }
  const d = new Date(ts);
  if (isNaN(d.getTime())) return String(ts);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hours = String(d.getHours()).padStart(2, '0');
  const mins = String(d.getMinutes()).padStart(2, '0');
  const secs = String(d.getSeconds()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${mins}:${secs}`;
};

// Helper to escape values for CSV RFC 4180 compliance
export const escapeCsvValue = (val) => {
  if (val === null || val === undefined) return '';
  const str = String(val);
  if (str.includes('"') || str.includes(',') || str.includes('\n') || str.includes('\r')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

// Helper to generate meaningful CSV file name
export const generateAuditLogFileName = (startDate, endDate) => {
  const fromStr = startDate ? startDate.replace(/[^\w-]/g, '_') : 'all';
  const toStr = endDate ? endDate.replace(/[^\w-]/g, '_') : 'all';
  return `audit_logs_${fromStr}_to_${toStr}.csv`;
};

// Helper to build CSV text content matching specifications
export const buildAuditLogCSV = (logs, filters = {}) => {
  const {
    startDate = '',
    endDate = '',
    selectedAction = 'ALL',
    selectedRole = 'ALL',
    search = ''
  } = filters;

  const fromDisplay = formatDateForFilter(startDate);
  const toDisplay = formatDateForFilter(endDate);
  const actionDisplay = selectedAction && selectedAction !== 'ALL' ? formatAction(selectedAction) : 'All Actions';
  const roleDisplay = selectedRole && selectedRole !== 'ALL' ? selectedRole : 'All Roles';
  const searchDisplay = search && search.trim() ? search.trim() : 'None';

  let csv = 'Audit Log Export\r\n';
  csv += `Filter From Date,${escapeCsvValue(fromDisplay)}\r\n`;
  csv += `Filter To Date,${escapeCsvValue(toDisplay)}\r\n`;
  csv += `Filter Action,${escapeCsvValue(actionDisplay)}\r\n`;
  csv += `Filter Role,${escapeCsvValue(roleDisplay)}\r\n`;
  csv += `Filter Search,${escapeCsvValue(searchDisplay)}\r\n\r\n`;

  csv += 'Timestamp,Actor,Target User,Action,Summary\r\n';

  logs.forEach(log => {
    const row = [
      escapeCsvValue(formatExactTimestamp(log.Timestamp)),
      escapeCsvValue(log.Actor || 'System'),
      escapeCsvValue(log.TargetUser || 'N/A'),
      escapeCsvValue(formatAction(log.Action)),
      escapeCsvValue(log.Summary || '')
    ];
    csv += row.join(',') + '\r\n';
  });

  return csv;
};

// Helper to trigger browser CSV file download
export const downloadCSVFile = (csvContent, fileName) => {
  const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
  if (typeof window !== 'undefined' && window.URL && typeof window.URL.createObjectURL === 'function') {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.addEventListener('click', (e) => {
      if (process.env.NODE_ENV === 'test') {
        e.preventDefault();
      }
    });
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    if (typeof window.URL.revokeObjectURL === 'function') {
      window.URL.revokeObjectURL(url);
    }
  }
};

const AdminAuditLogs = ({ user }) => {
  const [logs, setLogs] = useState([]);
  const [filterMeta, setFilterMeta] = useState({ actions: [], roles: [] });
  const [loading, setLoading] = useState(true);

  // Filter States - default date range set dynamically to today's date
  const [search, setSearch] = useState('');
  const [selectedAction, setSelectedAction] = useState('ALL');
  const [selectedRole, setSelectedRole] = useState('ALL');
  const [startDate, setStartDate] = useState(getTodayDateStr);
  const [endDate, setEndDate] = useState(getTodayDateStr);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Export State
  const [exportLoading, setExportLoading] = useState(false);
  const [exportNotice, setExportNotice] = useState('');

  // Modal State
  const [selectedLog, setSelectedLog] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Fetch Filter Metadata
  const fetchFilterMeta = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/audit-logs/filters`);
      const json = await res.json();
      if (json.status === 'success' && json.data) {
        setFilterMeta(json.data);
      }
    } catch (err) {
      console.error('Error loading audit log filters:', err);
    }
  }, []);

  // Fetch Audit Logs from API
  const fetchAuditLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.append('search', search.trim());
      if (selectedAction && selectedAction !== 'ALL') params.append('action', selectedAction);
      if (selectedRole && selectedRole !== 'ALL') params.append('actorRole', selectedRole);
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      params.append('page', page);
      params.append('limit', 15);

      const res = await fetch(`${API_BASE}/api/admin/audit-logs?${params.toString()}`);
      const json = await res.json();

      if (json.status === 'success' && json.data) {
        setLogs(json.data.logs || []);
        setTotalPages(json.data.totalPages || 1);
        setTotalCount(json.data.total || 0);
      }
    } catch (err) {
      console.error('Error fetching audit logs:', err);
    } finally {
      setLoading(false);
    }
  }, [search, selectedAction, selectedRole, startDate, endDate, page]);

  useEffect(() => {
    fetchFilterMeta();
  }, [fetchFilterMeta]);

  useEffect(() => {
    fetchAuditLogs();
  }, [fetchAuditLogs]);

  const handleResetFilters = () => {
    const today = getTodayDateStr();
    setSearch('');
    setSelectedAction('ALL');
    setSelectedRole('ALL');
    setStartDate(today);
    setEndDate(today);
    setPage(1);
    setExportNotice('');
  };

  const handleExportCSV = async () => {
    if (exportLoading) return;
    setExportNotice('');

    // Check if there are no records to export
    if (totalCount === 0 || (!loading && logs.length === 0)) {
      setExportNotice('No audit records found for the selected filters.');
      return;
    }

    const currentFilters = {
      startDate,
      endDate,
      selectedAction,
      selectedRole,
      search
    };

    // If all records are already within current logs (e.g. single page)
    if (totalPages <= 1 && logs.length >= totalCount) {
      const csvContent = buildAuditLogCSV(logs, currentFilters);
      const fileName = generateAuditLogFileName(startDate, endDate);
      downloadCSVFile(csvContent, fileName);
      return;
    }

    // Otherwise, fetch all matching records across pages
    setExportLoading(true);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.append('search', search.trim());
      if (selectedAction && selectedAction !== 'ALL') params.append('action', selectedAction);
      if (selectedRole && selectedRole !== 'ALL') params.append('actorRole', selectedRole);
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      params.append('limit', totalCount > 0 ? totalCount : 10000);
      params.append('page', 1);

      const res = await fetch(`${API_BASE}/api/admin/audit-logs?${params.toString()}`);
      const json = await res.json();

      if (json.status === 'success' && json.data && Array.isArray(json.data.logs)) {
        if (json.data.logs.length === 0) {
          setExportNotice('No audit records found for the selected filters.');
        } else {
          const csvContent = buildAuditLogCSV(json.data.logs, currentFilters);
          const fileName = generateAuditLogFileName(startDate, endDate);
          downloadCSVFile(csvContent, fileName);
        }
      } else {
        setExportNotice('No audit records found for the selected filters.');
      }
    } catch (err) {
      console.error('Error exporting audit logs:', err);
      setExportNotice('Failed to export audit logs. Please try again.');
    } finally {
      setExportLoading(false);
    }
  };

  const handleOpenDetails = (log) => {
    setSelectedLog(log);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedLog(null);
  };

  // Helper for Action Badge Styling
  const getActionBadgeClass = (action) => {
    if (!action) return 'neutral';
    const act = action.toUpperCase();
    if (act.includes('DEACTIVATED') || act.includes('DELETED') || act.includes('REJECTED') || act.includes('CLOSED')) {
      return 'danger';
    }
    if (act.includes('APPROVED') || act.includes('CREATED') || act.includes('REACTIVATED') || act.includes('OPENED')) {
      return 'success';
    }
    if (act.includes('SUBMITTED') || act.includes('UPLOADED') || act.includes('ASSIGNED')) {
      return 'info';
    }
    if (act.includes('UPDATED') || act.includes('WITHDRAWN')) {
      return 'warning';
    }
    return 'neutral';
  };

  // Helper for Role Badge Styling
  const getRoleBadgeClass = (role) => {
    if (!role) return 'system';
    const r = role.toLowerCase();
    if (r === 'admin' || r === 'editor') return 'admin';
    if (r === 'trainer') return 'trainer';
    if (r === 'learner') return 'learner';
    return 'system';
  };

  // Format Display Date/Time
  const formatTimestamp = (ts) => {
    if (!ts) return 'N/A';
    try {
      const d = new Date(ts);
      if (isNaN(d.getTime())) return ts;
      return d.toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      }).toUpperCase();
    } catch (e) {
      return ts;
    }
  };

  // Render Request Details Key-Value List
  const renderRequestDetails = (details) => {
    if (!details || (typeof details === 'object' && Object.keys(details).length === 0)) {
      return <span style={{ color: '#94a3b8', italic: 'true' }}>No request data available</span>;
    }

    if (typeof details !== 'object') {
      return <span>{String(details)}</span>;
    }

    return (
      <div className="audit-request-keyval-list">
        {Object.entries(details).map(([key, val]) => (
          <div key={key} style={{ marginBottom: '0.25rem' }}>
            <span className="audit-json-key">{key}: </span>
            <span className={typeof val === 'number' || typeof val === 'boolean' ? 'audit-json-val' : 'audit-json-str'}>
              {typeof val === 'object' ? JSON.stringify(val) : String(val)}
            </span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="admin-audit-logs-container">
      <div className="audit-logs-content">
        {/* Header Section */}
        <div className="audit-header-section">
          <div className="audit-header-title-group">
            <h1>Audit Logs</h1>
            <p className="audit-header-subtitle">
              System activity and administrative actions record
            </p>
          </div>
          <div className="audit-header-badge">
            <SvgIcon name="shield" size={16} />
            <span>Security Compliance Active</span>
          </div>
        </div>

        {/* Metrics Banner - Total Audit Records & Admin Actions */}
        <div className="audit-metrics-grid">
          {/* Card 1: Total Audit Records */}
          <div className="audit-metric-card audit-card-total" aria-label="Total Audit Records summary">
            <div className="audit-card-header">
              <div className="audit-metric-icon blue" aria-hidden="true">
                <SvgIcon name="fileText" size={22} />
              </div>
              <span className="audit-card-category-tag total">System Activity</span>
            </div>
            <div className="audit-metric-info">
              <span className="audit-metric-val">{totalCount}</span>
              <span className="audit-metric-lbl">Total Audit Records</span>
              <span className="audit-metric-desc">All recorded system activities</span>
            </div>
          </div>

          {/* Card 2: Admin Actions */}
          <div className="audit-metric-card audit-card-admin" aria-label="Admin Actions summary">
            <div className="audit-card-header">
              <div className="audit-metric-icon green" aria-hidden="true">
                <SvgIcon name="shieldCheck" size={22} />
              </div>
              <span className="audit-card-category-tag admin">Administrative</span>
            </div>
            <div className="audit-metric-info">
              <span className="audit-metric-val">
                {logs.filter(l => l.ActorRole === 'Admin' || l.ActorRole === 'Editor').length}
              </span>
              <span className="audit-metric-lbl">Admin Actions</span>
              <span className="audit-metric-desc">Actions performed by administrators</span>
            </div>
          </div>
        </div>

        {/* Filter Controls Panel */}
        <div className="audit-filter-panel">
          <div className="audit-filter-row">
            {/* Search Input */}
            <div className="audit-search-input-wrapper">
              <span className="audit-search-icon" aria-hidden="true">
                <SvgIcon name="search" size={16} />
              </span>
              <input
                id="audit-filter-search"
                type="text"
                placeholder="Search logs by actor, learner, action, or summary..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); setExportNotice(''); }}
              />
            </div>

            {/* Action Filter */}
            <select
              className="audit-select-field"
              value={selectedAction}
              onChange={(e) => { setSelectedAction(e.target.value); setPage(1); setExportNotice(''); }}
            >
              <option value="ALL">All Actions</option>
              {filterMeta.actions.map(act => (
                <option key={act} value={act}>{act.replace(/_/g, ' ')}</option>
              ))}
              {/* Fallback default options if DB returns empty initially */}
              {filterMeta.actions.length === 0 && (
                <>
                  <option value="ACCOUNT_DEACTIVATED">ACCOUNT DEACTIVATED</option>
                  <option value="ACCOUNT_CREATED">ACCOUNT CREATED</option>
                  <option value="REGISTRATION_APPROVED">REGISTRATION APPROVED</option>
                  <option value="PROJECT_TAG_CREATED">PROJECT TAG CREATED</option>
                  <option value="PAYMENT_SLIP_UPLOADED">PAYMENT SLIP UPLOADED</option>
                </>
              )}
            </select>

            {/* Role Filter */}
            <select
              className="audit-select-field"
              value={selectedRole}
              onChange={(e) => { setSelectedRole(e.target.value); setPage(1); setExportNotice(''); }}
            >
              <option value="ALL">All Roles</option>
              <option value="Admin">Admin</option>
              <option value="Trainer">Trainer</option>
              <option value="Learner">Learner</option>
              <option value="SYSTEM">System</option>
            </select>

            {/* Date Range Filters */}
            <div className="audit-date-group">
              <label htmlFor="audit-filter-from-date">From:</label>
              <input
                id="audit-filter-from-date"
                type="date"
                className="audit-date-input"
                value={startDate}
                onChange={(e) => { setStartDate(e.target.value); setPage(1); setExportNotice(''); }}
              />
            </div>

            <div className="audit-date-group">
              <label htmlFor="audit-filter-to-date">To:</label>
              <input
                id="audit-filter-to-date"
                type="date"
                className="audit-date-input"
                value={endDate}
                onChange={(e) => { setEndDate(e.target.value); setPage(1); setExportNotice(''); }}
              />
            </div>

            {/* Reset Button */}
            {(search || selectedAction !== 'ALL' || selectedRole !== 'ALL' || startDate !== getTodayDateStr() || endDate !== getTodayDateStr()) && (
              <button
                type="button"
                className="audit-reset-btn"
                onClick={handleResetFilters}
              >
                Clear Filters
              </button>
            )}
          </div>

          {/* Export Action Controls */}
          <div className="audit-filter-actions-row">
            {exportNotice ? (
              <div className="audit-export-notice" role="alert">
                <SvgIcon name="alertCircle" size={15} />
                <span>{exportNotice}</span>
              </div>
            ) : <div />}
            <button
              type="button"
              id="audit-export-csv-btn"
              className="audit-export-btn"
              onClick={handleExportCSV}
              disabled={exportLoading}
              aria-label="Export CSV"
            >
              <SvgIcon name="download" size={16} />
              <span>{exportLoading ? 'Exporting...' : 'Export CSV'}</span>
            </button>
          </div>
        </div>

        {/* Audit Table Card */}
        <div className="audit-table-card">
          <div className="audit-table-responsive">
            <table className="audit-table">
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>Actor</th>
                  <th>Target User</th>
                  <th>Action</th>
                  <th>Summary</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="6" style={{ textAlign: 'center', padding: '3rem' }}>
                      <div style={{ color: '#64748b', fontSize: '0.9rem' }}>Loading audit logs...</div>
                    </td>
                  </tr>
                ) : logs.length === 0 ? (
                  <tr>
                    <td colSpan="6">
                      <div className="audit-empty-state">
                        <div className="audit-empty-icon">
                          <SvgIcon name="search" size={28} />
                        </div>
                        <div className="audit-empty-text">
                          <h3>No audit records found</h3>
                          <p>Try adjusting your search terms or filter selections.</p>
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  logs.map((log) => (
                    <tr key={log.AuditLogID}>
                      <td className="audit-timestamp-cell">
                        {formatTimestamp(log.Timestamp)}
                      </td>
                      <td>
                        <span className={`audit-actor-badge ${getRoleBadgeClass(log.ActorRole)}`}>
                          <span>{log.ActorRole || 'System'}</span>
                          <span>•</span>
                          <span>{log.Actor}</span>
                        </span>
                      </td>
                      <td style={{ fontWeight: 500, color: log.TargetUser && log.TargetUser !== 'N/A' ? '#0f172a' : '#94a3b8' }}>
                        {log.TargetUser || 'N/A'}
                      </td>
                      <td>
                        <span className={`audit-action-badge ${getActionBadgeClass(log.Action)}`}>
                          {log.Action.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="audit-summary-cell">
                        {log.Summary}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <button
                          type="button"
                          className="audit-view-btn"
                          onClick={() => handleOpenDetails(log)}
                        >
                          <SvgIcon name="eye" size={14} />
                          <span>View Details</span>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Footer */}
          {!loading && logs.length > 0 && (
            <div className="audit-pagination-footer">
              <div>
                Showing page <strong>{page}</strong> of <strong>{totalPages}</strong> ({totalCount} total audit events)
              </div>
              <div className="audit-pagination-controls">
                <button
                  type="button"
                  className="audit-page-btn"
                  disabled={page <= 1}
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                >
                  Previous
                </button>

                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i + 1}
                    type="button"
                    className={`audit-page-btn ${page === i + 1 ? 'active' : ''}`}
                    onClick={() => setPage(i + 1)}
                  >
                    {i + 1}
                  </button>
                ))}

                <button
                  type="button"
                  className="audit-page-btn"
                  disabled={page >= totalPages}
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Audit Log Details Modal */}
      {isModalOpen && selectedLog && (
        <div className="audit-modal-backdrop" onClick={handleCloseModal}>
          <div className="audit-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="audit-modal-header">
              <h2>Audit Record Details</h2>
              <button
                type="button"
                className="audit-modal-close-btn"
                onClick={handleCloseModal}
              >
                &times;
              </button>
            </div>

            <div className="audit-modal-body">
              <div className="audit-detail-grid">
                <div className="audit-detail-item">
                  <span className="audit-detail-label">Timestamp</span>
                  <span className="audit-detail-val">{formatTimestamp(selectedLog.Timestamp)}</span>
                </div>

                <div className="audit-detail-item">
                  <span className="audit-detail-label">Action</span>
                  <span className={`audit-action-badge ${getActionBadgeClass(selectedLog.Action)}`} style={{ alignSelf: 'flex-start' }}>
                    {selectedLog.Action}
                  </span>
                </div>

                <div className="audit-detail-item">
                  <span className="audit-detail-label">Actor</span>
                  <div className="audit-detail-val" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <span className={`audit-actor-badge ${getRoleBadgeClass(selectedLog.ActorRole)}`}>
                      {selectedLog.ActorRole}
                    </span>
                    <span>{selectedLog.Actor}</span>
                  </div>
                </div>

                <div className="audit-detail-item">
                  <span className="audit-detail-label">Target User</span>
                  <span className="audit-detail-val">{selectedLog.TargetUser || 'N/A'}</span>
                </div>

                <div className="audit-detail-item">
                  <span className="audit-detail-label">Entity Type</span>
                  <span className="audit-detail-val">{selectedLog.EntityType || 'N/A'}</span>
                </div>

                <div className="audit-detail-item">
                  <span className="audit-detail-label">Entity ID</span>
                  <span className="audit-detail-val">{selectedLog.EntityID || 'N/A'}</span>
                </div>
              </div>

              <div className="audit-summary-box">
                <span className="audit-detail-label">Generated Summary</span>
                <p>{selectedLog.Summary}</p>
              </div>

              <div className="audit-request-details-container">
                <span className="audit-detail-label">Request / Query Details</span>
                {renderRequestDetails(selectedLog.RequestDetails)}
              </div>
            </div>

            <div className="audit-modal-footer">
              <button
                type="button"
                className="audit-modal-btn-close"
                onClick={handleCloseModal}
              >
                Close Details
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAuditLogs;
